import React, { useState, useEffect } from 'react';
import { Eye, Trash2, Mail, Phone, Calendar, User, MessageSquare, RefreshCw, Lock, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, sent
  const [copySuccess, setCopySuccess] = useState('');
  const [loginError, setLoginError] = useState('');

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(`${label} copiato!`);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setAuthToken('');
    setMessages([]);
    setSelectedMessage(null);
    setLoginError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsAuthenticated(true);
        setAuthToken(result.token);
        fetchMessages();
      } else {
        setLoginError(result.detail || 'Password non corretta');
      }
    } catch (error) {
      console.error('Errore durante il login:', error);
      setLoginError('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`);
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Errore caricamento messaggi:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    if (filter === 'new') return msg.status === 'new';
    if (filter === 'read') return msg.status === 'read' || msg.status === 'sent';
    return msg.status === filter;
  });

  const markAsRead = async (messageId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/${messageId}`, {
        method: 'PATCH',
      });
      // Update local state
      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, status: 'read' } : msg
      ));
    } catch (error) {
      console.error('Errore marcatura come letto:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo messaggio?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/${messageId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessages(messages.filter(msg => msg.id !== messageId));
        setSelectedMessage(null);
        alert('Messaggio eliminato con successo!');
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
      alert('Errore durante l\'eliminazione del messaggio');
    }
  };

  const handleOpenMessage = (message) => {
    setSelectedMessage(message);
    if (message.status === 'new') {
      markAsRead(message.id);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <div className="login-header">
            <Lock size={48} className="login-icon" />
            <h1>Pannello Admin</h1>
            <p>SI.SA. LINE S.r.l.</p>
          </div>
          <form onSubmit={handleLogin} className="login-form">
            <input
              type="password"
              placeholder="Password Admin"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              autoFocus
              disabled={loading}
            />
            {loginError && (
              <div className="login-error">
                {loginError}
              </div>
            )}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Verifica in corso...' : 'Accedi'}
            </button>
          </form>
        </div>

        <style jsx>{`
          .admin-login {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #004B93, #FF6B35);
            padding: 20px;
          }

          .login-container {
            background: white;
            padding: 3rem;
            border-radius: 1rem;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            max-width: 400px;
            width: 100%;
          }

          .login-header {
            text-align: center;
            margin-bottom: 2rem;
          }

          .login-icon {
            color: var(--brand-orange);
            margin-bottom: 1rem;
          }

          .login-header h1 {
            font-size: 1.75rem;
            font-weight: 800;
            color: var(--text-primary);
            margin-bottom: 0.5rem;
          }

          .login-header p {
            color: var(--text-secondary);
          }

          .login-form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .login-input {
            padding: 1rem;
            border: 2px solid var(--border-light);
            border-radius: 0.5rem;
            font-size: 1rem;
            transition: border-color 0.3s;
          }

          .login-input:focus {
            outline: none;
            border-color: var(--brand-orange);
          }

          .login-error {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
            padding: 0.75rem;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            border: 1px solid rgba(239, 68, 68, 0.2);
          }

          .login-input:disabled,
          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <div className="admin-branding">
              <img 
                src="https://customer-assets.emergentagent.com/job_1339b4ee-79eb-48d1-9f64-307ac7401951/artifacts/shomcg6m_Logo3.jpg" 
                alt="SI.SA. LINE" 
                className="admin-logo"
              />
              <div>
                <h1 className="admin-title">Pannello Admin</h1>
                <p className="admin-subtitle">Messaggi dal Form Contatti</p>
              </div>
            </div>
            <div className="admin-actions">
              <button onClick={fetchMessages} className="btn-refresh" disabled={loading}>
                <RefreshCw size={20} className={loading ? 'spinning' : ''} />
                Aggiorna
              </button>
              <button 
                onClick={handleLogout}
                className="btn-logout"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-content container">
        {/* Filters */}
        <div className="admin-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Tutti ({messages.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'new' ? 'active' : ''}`}
            onClick={() => setFilter('new')}
          >
            Nuovi ({messages.filter(m => m.status === 'new').length})
          </button>
          <button 
            className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
            onClick={() => setFilter('read')}
          >
            Letti ({messages.filter(m => m.status === 'read' || m.status === 'sent').length})
          </button>
        </div>

        {/* Messages List */}
        <div className="messages-grid">
          {loading ? (
            <div className="loading">Caricamento messaggi...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="no-messages">
              <MessageSquare size={64} />
              <p>Nessun messaggio trovato</p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div 
                key={message.id} 
                className="message-card"
                onClick={() => handleOpenMessage(message)}
              >
                <div className="message-header">
                  <div className="message-status">
                    <span className={`status-badge ${message.status}`}>
                      {message.status === 'new' ? 'Nuovo' : message.status === 'read' ? 'Letto' : 'Inviato'}
                    </span>
                  </div>
                  <div className="message-date">
                    <Calendar size={14} />
                    {formatDate(message.timestamp)}
                  </div>
                </div>

                <div className="message-info">
                  <div className="info-row">
                    <User size={16} />
                    <strong>{message.name}</strong>
                  </div>
                  <div className="info-row">
                    <Mail size={16} />
                    {message.email}
                  </div>
                  {message.phone && (
                    <div className="info-row">
                      <Phone size={16} />
                      {message.phone}
                    </div>
                  )}
                </div>

                <div className="message-subject">
                  <strong>Oggetto:</strong> {message.subject}
                </div>

                <div className="message-preview">
                  {message.message.substring(0, 100)}
                  {message.message.length > 100 && '...'}
                </div>

                <button className="btn-view">
                  <Eye size={16} />
                  Visualizza Dettagli
                </button>

                <button 
                  className="btn-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMessage(message.id);
                  }}
                >
                  <Trash2 size={16} />
                  Elimina
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dettagli Messaggio</h2>
              <button onClick={() => setSelectedMessage(null)} className="modal-close">
                ×
              </button>
            </div>

            <div className="modal-body">
              {copySuccess && (
                <div className="copy-success">
                  <Check size={16} />
                  {copySuccess}
                </div>
              )}

              <div className="detail-section">
                <h3>Informazioni Contatto</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Nome:</span>
                    <span className="detail-value">{selectedMessage.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <div className="copy-field">
                      <a href={`mailto:${selectedMessage.email}`} className="detail-value link">
                        {selectedMessage.email}
                      </a>
                      <button 
                        onClick={() => copyToClipboard(selectedMessage.email, 'Email')}
                        className="copy-btn"
                        title="Copia email"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                  {selectedMessage.phone && (
                    <div className="detail-item">
                      <span className="detail-label">Telefono:</span>
                      <div className="copy-field">
                        <a href={`tel:${selectedMessage.phone}`} className="detail-value link">
                          {selectedMessage.phone}
                        </a>
                        <button 
                          onClick={() => copyToClipboard(selectedMessage.phone, 'Telefono')}
                          className="copy-btn"
                          title="Copia telefono"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Data:</span>
                    <span className="detail-value">{formatDate(selectedMessage.timestamp)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Stato:</span>
                    <span className={`status-badge ${selectedMessage.status}`}>
                      {selectedMessage.status === 'new' ? 'Nuovo' : selectedMessage.status === 'read' ? 'Letto' : 'Email Inviata'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Oggetto</h3>
                <p className="detail-subject">{selectedMessage.subject}</p>
              </div>

              <div className="detail-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Messaggio</h3>
                  <button 
                    onClick={() => copyToClipboard(selectedMessage.message, 'Messaggio')}
                    className="copy-btn-large"
                  >
                    <Copy size={16} />
                    Copia Messaggio
                  </button>
                </div>
                <p className="detail-message">{selectedMessage.message}</p>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                onClick={() => {
                  const subject = `Re: ${selectedMessage.subject}`;
                  const body = `Gentile ${selectedMessage.name},%0D%0A%0D%0AGrazie per averci contattato.%0D%0A%0D%0ACordiali saluti,%0D%0ASI.SA. LINE S.r.l.`;
                  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(selectedMessage.email)}&su=${encodeURIComponent(subject)}&body=${body}`;
                  window.open(gmailUrl, '_blank');
                }}
                className="btn-primary"
              >
                <Mail size={18} />
                Rispondi con Gmail
              </button>
              <button 
                onClick={() => {
                  const subject = `Re: ${selectedMessage.subject}`;
                  const body = `Gentile ${selectedMessage.name},%0D%0A%0D%0AGrazie per averci contattato.%0D%0A%0D%0ACordiali saluti,%0D%0ASI.SA. LINE S.r.l.`;
                  const outlookUrl = `https://outlook.office365.com/mail/deeplink/compose?to=${encodeURIComponent(selectedMessage.email)}&subject=${encodeURIComponent(subject)}&body=${body}`;
                  window.open(outlookUrl, '_blank');
                }}
                className="btn-primary"
                style={{ background: 'var(--brand-blue)' }}
              >
                <Mail size={18} />
                Rispondi con Outlook
              </button>
              <button 
                onClick={() => {
                  if (window.confirm('Sei sicuro di voler eliminare questo messaggio?')) {
                    deleteMessage(selectedMessage.id);
                  }
                }}
                className="btn-delete-modal"
              >
                <Trash2 size={18} />
                Elimina Messaggio
              </button>
              <button onClick={() => setSelectedMessage(null)} className="btn-secondary">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .admin-page {
          min-height: 100vh;
          background: var(--bg-page);
        }

        .admin-header {
          background: var(--bg-card);
          border-bottom: 3px solid var(--brand-blue);
          padding: 1.5rem 0;
          margin-bottom: 2rem;
        }

        .admin-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .admin-branding {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .admin-logo {
          width: 60px;
          height: 60px;
          object-fit: contain;
        }

        .admin-title {
          font-size: 1.75rem;
          font-weight: 800;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .admin-subtitle {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }

        .admin-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .btn-refresh {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--brand-blue);
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-refresh:hover:not(:disabled) {
          background: var(--brand-blue-hover);
          transform: translateY(-2px);
        }

        .btn-refresh:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-logout {
          padding: 0.75rem 1.5rem;
          background: var(--brand-orange);
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          font-size: 0.95rem;
        }

        .btn-logout:hover {
          background: var(--brand-orange-hover);
          transform: translateY(-2px);
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .admin-content {
          padding-bottom: 3rem;
        }

        .admin-filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid var(--border-light);
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-btn:hover {
          border-color: var(--brand-orange);
        }

        .filter-btn.active {
          background: var(--brand-orange);
          color: white;
          border-color: var(--brand-orange);
        }

        .messages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        @media (max-width: 768px) {
          .messages-grid {
            grid-template-columns: 1fr;
          }
        }

        .message-card {
          background: white;
          border: 1px solid var(--border-light);
          border-radius: 0.75rem;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          cursor: pointer;
          transition: all 0.3s;
        }

        .message-card:hover {
          border-color: var(--brand-orange);
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.1);
        }

        .message-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .status-badge.new {
          background: #fef3c7;
          color: #92400e;
        }

        .status-badge.sent {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.read {
          background: #dbeafe;
          color: #1e40af;
        }

        .message-date {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--text-muted);
        }

        .message-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 1rem;
          background: var(--bg-section-alternate);
          border-radius: 0.5rem;
        }

        .info-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          color: var(--text-secondary);
        }

        .info-row svg {
          color: var(--brand-blue);
        }

        .message-subject {
          font-size: 0.95rem;
          color: var(--text-primary);
        }

        .message-preview {
          font-size: 0.9rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .btn-view {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--brand-orange);
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-view:hover {
          background: var(--brand-orange-hover);
        }

        .btn-delete {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          margin-top: 0.5rem;
        }

        .btn-delete:hover {
          background: #dc2626;
        }

        .btn-delete-modal {
          padding: 0.75rem 1.5rem;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
        }

        .btn-delete-modal:hover {
          background: #dc2626;
        }

        .loading,
        .no-messages {
          grid-column: 1 / -1;
          text-align: center;
          padding: 4rem 2rem;
          color: var(--text-secondary);
        }

        .no-messages {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .no-messages svg {
          color: var(--brand-blue);
          opacity: 0.3;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1rem;
        }

        .modal-content {
          background: white;
          border-radius: 1rem;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--border-light);
        }

        .modal-header h2 {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--text-primary);
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 2rem;
          color: var(--text-muted);
          cursor: pointer;
          line-height: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.25rem;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: var(--bg-card);
          color: var(--text-primary);
        }

        .modal-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .detail-section h3 {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        @media (max-width: 640px) {
          .detail-grid {
            grid-template-columns: 1fr;
          }
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .detail-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .detail-value {
          font-size: 0.95rem;
          color: var(--text-primary);
        }

        .detail-value.link {
          color: var(--brand-blue);
          text-decoration: none;
        }

        .detail-value.link:hover {
          text-decoration: underline;
        }

        .detail-subject {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .detail-message {
          white-space: pre-wrap;
          line-height: 1.8;
          color: var(--text-secondary);
          background: var(--bg-section-alternate);
          padding: 1rem;
          border-radius: 0.5rem;
        }

        .modal-footer {
          padding: 1.5rem;
          border-top: 1px solid var(--border-light);
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .modal-footer {
            grid-template-columns: 1fr;
          }
        }

        .modal-footer .btn-primary,
        .modal-footer .btn-secondary {
          width: 100%;
        }

        /* Copy functionality */
        .copy-success {
          background: #d1fae5;
          color: #065f46;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .copy-field {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .copy-btn,
        .copy-btn-large {
          background: var(--brand-blue);
          color: white;
          border: none;
          padding: 0.5rem;
          border-radius: 0.375rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .copy-btn-large {
          padding: 0.5rem 1rem;
        }

        .copy-btn:hover,
        .copy-btn-large:hover {
          background: var(--brand-blue-hover);
          transform: translateY(-1px);
        }

        .copy-btn:active,
        .copy-btn-large:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default Admin;
